"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, ShieldCheck, Clock, Wrench, Zap, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const keyHighlights = [{
  text: "Fast Diagnosis & Same-Day Repair"
}, {
  text: "Element & Thermostat Replacements"
}, {
  text: "Standard & Hybrid Tank Installations"
}, {
  text: "Licensed & Insured Local Plumbers"
}];
export default function ElectricHeaterHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="electricwaterheaterhero" data-nav="dark" className="relative overflow-hidden bg-primary text-primary-foreground py-16 md:py-24 lg:py-28">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <div className="absolute -top-24 -right-24 size-96 rounded-full bg-accent blur-3xl" />
        <div className="absolute -bottom-24 -left-24 size-96 rounded-full bg-chart-3 blur-3xl" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" animate="visible" variants={containerVariants} className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left / Main Content Column */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="bg-accent/20 border-accent/40 text-accent font-semibold px-3 py-1 uppercase tracking-wider text-xs">
                <Zap className="size-3.5 mr-1" />
                Jonesboro Electric Water Heater Specialists
              </Badge>
              <span className="inline-flex items-center gap-1.5 text-xs text-primary-foreground/80 font-medium">
                <Clock className="size-3.5 text-accent" />
                24/7 Rapid Emergency Response
              </span>
            </motion.div>

            <motion.h1 variants={itemVariants} className="font-default text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-primary-foreground">
              Electric Water Heater Repair, Replacement &amp; Installation in Jonesboro, GA
            </motion.h1>

            <motion.p variants={itemVariants} className="text-base sm:text-lg md:text-xl text-primary-foreground/90 font-normal leading-relaxed max-w-2xl">
              From faulty heating elements and tripped circuit breakers to complete tank replacements, our licensed Jonesboro technicians deliver rapid troubleshooting, precise repairs, energy-efficient replacements, and routine maintenance to restore your hot water fast.
            </motion.p>

            {/* CTAs */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shadow-lg text-base h-12 px-6">
                <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                  <Phone className="size-5" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>

              <Button asChild size="lg" variant="secondary" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-semibold shadow-md text-base h-12 px-6">
                <Link to="/contact/" className="flex items-center justify-center gap-2">
                  <span>Request Service</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </motion.div>

            {/* Checklist Highlights */}
            <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4 border-t border-primary-foreground/15">
              {keyHighlights.map((highlight, index) => <div key={index} data-index={index} className="flex items-center gap-2 text-sm text-primary-foreground/90 font-medium">
                  <CheckCircle2 className="size-4 text-accent shrink-0" />
                  <span>{highlight.text}</span>
                </div>)}
            </motion.div>
          </div>

          {/* Right / Visual Card Feature */}
          <motion.div variants={itemVariants} className="lg:col-span-5">
            <div className="relative rounded-2xl bg-card text-card-foreground p-6 sm:p-8 shadow-2xl border border-border">
              <div className="flex items-center justify-between pb-6 border-b border-border">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Available 24 Hours
                  </span>
                  <h2 className="text-xl font-bold text-card-foreground font-default">
                    Emergency Water Heater Care
                  </h2>
                </div>
                <div className="size-12 rounded-xl bg-accent/15 flex items-center justify-center text-accent">
                  <Wrench className="size-6" />
                </div>
              </div>

              <div className="space-y-4 py-6">
                <div className="flex items-start gap-3">
                  <div className="size-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0 mt-0.5">
                    <Zap className="size-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-card-foreground">No Hot Water or Cold Water Only</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Diagnostics for upper and lower heating elements, limit switches, and electrical thermostats.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="size-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0 mt-0.5">
                    <ShieldCheck className="size-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-card-foreground">Leaking Tanks &amp; Pressure Valves</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Relief valve maintenance, pipe connection sealing, and honest replacement assessments.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="size-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0 mt-0.5">
                    <Clock className="size-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-card-foreground">Energy-Efficient New Installations</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Expert sizing and code-compliant installations for standard and hybrid electric models.
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-border flex flex-col gap-3">
                <div className="bg-muted p-3.5 rounded-lg flex items-center justify-between text-xs font-medium">
                  <span className="text-muted-foreground">Serving All Clayton County</span>
                  <span className="text-card-foreground font-semibold">Jonesboro, GA</span>
                </div>
                <Button asChild variant="outline" className="w-full justify-center border-border hover:bg-muted text-card-foreground font-semibold">
                  <Link to="/water-heater/">
                    Explore All Water Heater Services
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}